import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Breadcrumbs, Card, Chip, EmptyState, Pagination } from '../../components';
import useDocumentHead from '../../hooks/useDocumentHead';
import { useLang, type Lang } from '../../i18n';
import { get } from '../../services/api';
import './BlogPage.scss';

interface Post {
  slug: string;
  title: string;
  excerpt?: string;
  cover?: string;
  cover_image?: string;
  published_at?: string;
  reading_time?: number;
  tags?: string[];
}

const PER_PAGE = 9;
const LANG_MAP: Record<Lang, string> = { 'uz-Latn': 'uz_lat', 'uz-Cyrl': 'uz_cyr', ru: 'ru', en: 'en' };

export default function BlogPage() {
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const tag = params.get('tag');
  const page = parseInt(params.get('page') || '1', 10);
  const [items, setItems] = useState<Post[]>([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let cancel = false;
    (async () => {
      const qs = new URLSearchParams({ page: String(page), page_size: String(PER_PAGE), lang: LANG_MAP[lang] });
      if (tag) qs.set('tag', tag);
      try {
        const res = await get<{ results?: Post[]; items?: Post[]; count?: number; total?: number }>(`/blog/?${qs}`);
        if (!cancel) {
          setItems(res?.results || res?.items || []);
          setTotal(res?.count ?? res?.total ?? 0);
        }
      } catch {
        if (!cancel) { setItems([]); setTotal(0); }
      }
    })();
    return () => { cancel = true; };
  }, [tag, page, lang]);

  const tags = useMemo(() => {
    const set = new Set<string>();
    for (const p of items) for (const tg of p.tags || []) set.add(tg);
    return Array.from(set);
  }, [items]);

  useDocumentHead({
    title: `${t('blog.title')} — ${t('brand.name')}`,
    description: t('blog.sub'),
    canonical: 'https://ukam.uz/blog',
    og: { title: t('blog.title'), description: t('blog.sub'), type: 'website' },
    schemaOrg: { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('brand.name'), item: 'https://ukam.uz/' },
      { '@type': 'ListItem', position: 2, name: t('blog.title'), item: 'https://ukam.uz/blog' },
    ] },
  });

  const setPage = (p: number) => {
    const next = new URLSearchParams(params);
    next.set('page', String(p));
    setParams(next);
  };

  const setTag = (next: string | null) => {
    const np = new URLSearchParams(params);
    if (next) np.set('tag', next); else np.delete('tag');
    np.delete('page');
    setParams(np);
  };

  return (
    <div className="blog-page">
      <div className="container blog-page__head">
        <Breadcrumbs items={[{ label: t('brand.name'), href: '/' }, { label: t('blog.title') }]} />
        <h1 className="blog-page__title">{t('blog.title')}</h1>
        <p className="blog-page__sub">{t('blog.sub')}</p>
        {tags.length > 0 && (
          <div className="blog-page__tags blog-page__tags--buttons">
            <button
              type="button"
              className={`blog-page__tag-btn${!tag ? ' blog-page__tag-btn--active' : ''}`}
              onClick={() => setTag(null)}
            >
              {t('services.all')}
            </button>
            {tags.map((tg) => (
              <button
                key={tg}
                type="button"
                className={`blog-page__tag-btn${tag === tg ? ' blog-page__tag-btn--active' : ''}`}
                onClick={() => setTag(tg)}
              >
                {tg}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="container blog-page__body">
        {items.length === 0 ? (
          <EmptyState title={t('services.empty')} />
        ) : (
          <div className="grid grid--3">
            {items.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="blog-page__card">
                <Card variant="bordered" padding="md" className="blog-page__card-inner">
                  {(p.cover || p.cover_image) && <div className="blog-page__cover"><img src={p.cover || p.cover_image} alt="" /></div>}
                  <h3 className="blog-page__card-title">{p.title}</h3>
                  {p.excerpt && <p className="blog-page__excerpt">{p.excerpt}</p>}
                  <div className="blog-page__meta">
                    {p.published_at && <span>{p.published_at}</span>}
                    {p.reading_time && <span>{t('blog.reading_time', { minutes: p.reading_time })}</span>}
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
        {total > PER_PAGE && (
          <div className="blog-page__pagination">
            <Pagination page={page} total={Math.ceil(total / PER_PAGE)} onChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}
